import { Badge } from "@/common/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";
import { formatTimestamp } from "@/lib/utils";

import type { GuardrailEvent } from "./guardrail-metrics-types";

interface EventsTableProps {
  events: GuardrailEvent[];
  selectedEvent: GuardrailEvent | null;
  onSelect: (event: GuardrailEvent) => void;
}

export function EventsTable({
  events,
  selectedEvent,
  onSelect,
}: EventsTableProps): React.ReactElement {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Session</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Policies</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event, index) => {
          const isSelected =
            selectedEvent?.session_id === event.session_id &&
            selectedEvent?.timestamp === event.timestamp;

          return (
            <TableRow
              key={`${event.session_id}-${event.timestamp}-${index}`}
              data-testid={`event-row-${index}`}
              className={`cursor-pointer ${isSelected ? "bg-muted" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => onSelect(event)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(event);
                }
              }}
            >
              <TableCell className="whitespace-nowrap text-xs">
                {formatTimestamp(event.timestamp)}
              </TableCell>
              <TableCell className="max-w-[150px] truncate font-mono text-xs">
                {event.user_id}
              </TableCell>
              <TableCell className="max-w-[120px] truncate font-mono text-xs">
                {event.session_id.slice(0, 8)}
              </TableCell>
              <TableCell>
                <Badge
                  variant={
                    event.guardrail_intervention.source === "input" ? "default" : "secondary"
                  }
                >
                  {event.guardrail_intervention.source}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="destructive">{event.guardrail_intervention.action}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {event.guardrail_intervention.policies.map((p) => (
                    <Badge key={p} variant="outline" className="text-xs">
                      {p}
                    </Badge>
                  ))}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
